/**
 * Market store — composed from domain slices.
 *
 * Architecture:
 *   liveDataSlice     → hot-path chain/LTP/meta (2s/60s ticks)
 *   expirySlice       → per-symbol expiry lists (rarely changes)
 *   historySlice      → intraday/historical/range/cog/marketwatch/analysis
 *   tradeAlertSlice   → global trade signal popup UI state
 *
 * All slices are composed into a single Zustand store so that atomic actions
 * (e.g. setMasterComputeAll) work correctly. Consumers import `useMarketStore`
 * from `../store` and are unaware of the slice decomposition.
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { createLiveDataSlice } from './liveDataSlice'
import { createExpirySlice } from './expirySlice'
import { createHistorySlice } from './historySlice'
import { createTradeAlertSlice } from './tradeAlertSlice'

// Re-export the combined store type and domain value types for consumers.
export type { MarketStore, OptionChainRow, OptionChainDetail, MarketMeta, MarketMetrics } from './types'

export const useMarketStore = create<import('./types').MarketStore>()(
    devtools(
        (...args) => ({
            ...createLiveDataSlice(...args),
            ...createExpirySlice(...args),
            ...createHistorySlice(...args),
            ...createTradeAlertSlice(...args),
        }),
        { name: 'MarketStore' },
    ),
)
